import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, RotateCcw, History, X, Repeat, Package, Plus, Printer, Upload, Video } from "lucide-react";
import { PageHeader, PageBody, Field, StatusPill, statusTone } from "@/components/PageChrome";
import { Input, Select, Textarea, Button } from "@/components/controls";
import ImageInput from "@/components/ImageInput";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import SkuTimelineTrends from "@/components/SkuTimelineTrends";
import ShippingExtras from "@/components/ShippingExtras";
import ImageThumb from "@/components/ImageThumb";

// Aug 3 2026 — fields that an allocated order mirrors from its source
// procurement record get a small "updated via procurement" note plus a link to
// that record. Mirrors PROCUREMENT_SYNCED_FIELDS in backend/routes_orders.py:
// only annotate a field the backend actually keeps in step, otherwise the note
// would claim something untrue. Hoisted, so it can be referenced by the field
// definitions below.
function procHint(o, key) {
  const evs = o?.procurement_allocations || [];
  const ev = evs[evs.length - 1];
  if (!ev || !ev.procurement_id) return null;
  // Being linked to a procurement row is not the same as this field having been
  // filled from it. Without this the badge claims "updated via procurement" next
  // to fields that are still blank.
  if (key) {
    const v = o?.[key];
    if (v === null || v === undefined || v === "") return null;
  }
  const kind = ev.procurement_source === "orders" ? "order" : "procurement";
  // Mirror of the backend's fill-only rule (routes_orders.py, sync_from_allocated
  // _procurement): when the source is another ORDER, these two are never
  // overwritten, because the order has its own lifecycle for them. So their value
  // is the user's own and claiming "updated via ..." here would be false.
  if (kind === "order" && (key === "china_order_status" || key === "china_shipment_id")) {
    return null;
  }
  const where = kind === "order" ? "order-linked procurement" : "procurement";
  return {
    text: `updated via ${where} · China No. ${ev.china_id ?? "?"}`,
    target: { kind, id: ev.procurement_id },
  };
}

// Feb 27 2026 — coerce any incoming date string (ISO-with-time, `dd-mmm-yyyy`,
// Flipkart's `Aug 02, 2026`, `dd/mm/yyyy`, `dd-mm-yyyy`) to a bare ISO
// `YYYY-MM-DD` so <input type="date"> can display it. Returns "" for
// null/blank/unparseable so the field renders empty rather than "Invalid Date".
const _MONTHS_SHORT = { jan:1, feb:2, mar:3, apr:4, may:5, jun:6, jul:7, aug:8, sep:9, oct:10, nov:11, dec:12,
                         january:1, february:2, march:3, april:4, june:6, july:7, august:8,
                         september:9, october:10, november:11, december:12 };
function _toIsoDate(v) {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v).trim();
  if (!s) return "";
  // ISO already — take the date portion.
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // dd-mmm-yy(yy) or dd Mon yyyy
  m = s.match(/^(\d{1,2})[\s\-]([A-Za-z]{3,})[\s\-,]+(\d{2,4})/);
  if (m) {
    const mo = _MONTHS_SHORT[m[2].toLowerCase()];
    if (mo) {
      let y = Number(m[3]); if (y < 100) y += 2000;
      return `${y}-${String(mo).padStart(2, "0")}-${String(Number(m[1])).padStart(2, "0")}`;
    }
  }
  // "Aug 02, 2026" / "Aug 2 2026" / "August 02, 2026" (also with trailing time)
  m = s.match(/^([A-Za-z]{3,})\s+(\d{1,2}),?\s+(\d{4})/);
  if (m) {
    const mo = _MONTHS_SHORT[m[1].toLowerCase()];
    if (mo) {
      return `${m[3]}-${String(mo).padStart(2, "0")}-${String(Number(m[2])).padStart(2, "0")}`;
    }
  }
  // dd/mm/yyyy · dd-mm-yyyy · dd/mm/yy
  m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    let y = Number(m[3]); if (y < 100) y += 2000;
    const d = Number(m[1]), mo = Number(m[2]);
    if (mo >= 1 && mo <= 12) {
      return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  return "";
}

// Feb 27 2026 — IST (Asia/Kolkata) timestamp formatter, mirrors the one used
// on the Logs page so timezones stay consistent across the app. Returns
// `DD-MMM-YYYY HH:mm:ss`. Falls back to the raw ISO on parse failure.
function _fmtIST(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(d).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
}

/* Field spec grouped by section */
const SECTIONS = [
  {
    key: "general", title: "General", fields: [
      { k: "marketplace", label: "Marketplace", type: "select", opts: (d) => d.marketplace },
      { k: "account_id", label: "Account ID" },
      { k: "marketplace_identifier", label: "Marketplace Identifier" },
      { k: "product_url", label: "Product URL" },
      { k: "image_url", label: "Image URL", type: "image", span: 2 },
      { k: "product_name", label: "Product Name", span: 2 },
      { k: "system_sku", label: "System SKU (auto-mapped)" },
      { k: "working_status", label: "Working Status", type: "select", opts: (d) => d.working_status },
      { k: "electrical_type", label: "Electrical/Non-Electrical", type: "select", opts: (d) => d.electrical_type },
      { k: "gst_rate", label: "GST Rate %", type: "number" },
      { k: "amazon_commission", label: "Amazon Commission ₹", type: "number" },
      { k: "amazon_payout", label: "Amazon Payout ₹", type: "number" },
      { k: "final_importrend_margin", label: "Importrend Margin ₹", type: "number", readonly: true, mono: true },
      { k: "final_transfer_price", label: "Final Transfer Price ₹", type: "number" },
      { k: "importrend_status", label: "Importrend Status", type: "select", opts: (d) => d.importrend_status },
      { k: "marketplace_status", label: "Marketplace Status", type: "select", opts: (d) => d.marketplace_status },
      { k: "return_received_status", label: "Return Status", type: "select", opts: (d) => d.return_received_status },
      { k: "refund_status", label: "Refund Status", type: "select", opts: (d) => d.refund_status },
      { k: "product_condition", label: "Product Condition", type: "select", opts: (d) => d.product_condition },
      { k: "payment_method", label: "Payment Method", type: "select", opts: (d) => d.payment_method || ["COD", "Prepaid"] },
      { k: "tags", label: "Tags", span: 2 },
      { k: "comments", label: "Comments", type: "textarea", span: 2 },
      // Physical dims — auto-fetched from Inventory (by system_sku) on create / SKU update
      { k: "product_type", label: "Product Type", type: "select", opts: (d) => d.product_type },
      { k: "length_cm", label: "Length (cm)", type: "number" },
      { k: "width_cm", label: "Width (cm)", type: "number" },
      { k: "height_cm", label: "Height (cm)", type: "number" },
      { k: "weight_gram", label: "Weight (gm)", type: "number" },
    ],
  },
  {
    key: "claim", title: "Claim", fields: [
      { k: "claim_status", label: "Claim Status", type: "select", opts: (d) => d.claim_status },
      { k: "claim_amount", label: "Claim Amount ₹", type: "number" },
      { k: "claim_raised_date", label: "Claim Raised Date", type: "date" },
      { k: "claim_comments", label: "Claim Comments", type: "textarea", span: 2 },
    ],
  },
  {
    key: "shipping", title: "Shipping", fields: [
      { k: "shipping_status", label: "Shipping Status", type: "select", opts: (d) => d.shipping_status },
      { k: "shipping_date", label: "Shipping Date", type: "date" },
      { k: "courier_name", label: "Shipping Courier Name" },
      { k: "tracking_id", label: "Shipping Tracking ID" },
      // Feb 25 2026 — freight charges per shipment (base attempt).
      { k: "shipping_forward_charges", label: "Forward Charges ₹", type: "number" },
      { k: "shipping_rto_charges", label: "RTO Charges ₹", type: "number" },
      { k: "shipping_reverse_charges", label: "Reverse Charges ₹", type: "number" },
    ],
  },
  {
    key: "price_update", title: "Price Update", fields: [
      { k: "calculated_sp", label: "Suggestive SP ₹ (from China Procurement)", readonly: true, mono: true },
      { k: "updated_price", label: "Updated Price ₹", type: "number" },
      { k: "reseller_updated_price", label: "Reseller Updated Price ₹", type: "number" },
      { k: "price_updated_status", label: "Price Updated Status", type: "select", opts: (d) => d.price_updated_status },
    ],
  },
  {
    key: "china", title: "China Procurement", fields: [
      { k: "china_id", label: "China No", readonly: true, mono: true },
      { k: "global_rmb_conversion_rate", label: "RMB Conversion", type: "number" },
      { k: "international_freight_rate", label: "International Freight Charges", type: "number" },
      { k: "aggregator_commission_percentage", label: "Aggregator Commission %", type: "number" },
      // Sticky per-order overrides — blank falls back to global on save/recompute.
      { k: "china_margin_percentage", label: "Importrend Margin %", type: "number" },
      { k: "china_reseller_margin_percentage", label: "Reseller Margin %", type: "number" },
      { k: "source_url", label: "Source URL", hint: (o, k) => {
        // A live procurement mirror wins over the one-time LIFO auto-fill,
        // because it is what the value currently tracks.
        const p = procHint(o, k);
        if (p) return p;
        const s = o.lifo_source;
        if (!s || !s.kind) return null;
        const label = s.kind === "procurement" ? "procurement" :
                      s.kind === "order" ? "prior order" : "inventory";
        return { text: `auto-filled from ${label} · #${s.ref_no ?? "?"}`, target: s };
      } },
      { k: "variant", label: "Variant" },
      { k: "china_price", label: "China Price ¥", type: "number", hint: procHint },
      { k: "china_shipping", label: "China Shipping ¥", type: "number", hint: procHint },
      { k: "moq", label: "MOQ", type: "number", hint: procHint },
      { k: "china_weight_gram", label: "Weight (gm)", type: "number", hint: procHint },
      { k: "china_qty_to_order", label: "Qty to Order", type: "number" },
      { k: "china_order_status", label: "China Status", type: "select", opts: (d) => d.china_order_status, hint: procHint },
      { k: "china_order_id", label: "China Order ID", hint: procHint },
      { k: "china_shipment_id", label: "China Shipment ID", hint: procHint },
      { k: "china_order_date", label: "China Order Date", type: "date", hint: procHint },
      { k: "china_order_received_date", label: "China Received Date", type: "date", hint: procHint },
      { k: "china_order_dispatched_date", label: "China Dispatched Date", type: "date", hint: procHint },
      { k: "china_image_url", label: "China Image URL", type: "image", span: 2 },
      { k: "china_comments", label: "China Comments", type: "textarea", span: 2 },
      { k: "china_margin", label: "Importrend Margin ₹", type: "number", mono: true },
      { k: "china_reseller_margin", label: "Reseller Margin ₹", type: "number", mono: true },
      { k: "china_transfer_price", label: "Transfer Price ₹", type: "number", mono: true },
      { k: "buy_back_price", label: "Buy-Back Price ₹", type: "number", mono: true },
      // Computed fields — now editable (backend `respect_overrides` preserves manual values)
      { k: "china_costing", label: "China Costing ₹", type: "number", mono: true },
      { k: "china_international_freight_charges", label: "Intl Freight Charges ₹", type: "number", mono: true },
      { k: "china_aggregator_margin", label: "Aggregator Commission ₹", type: "number", mono: true },
      { k: "china_gst", label: "GST ₹", type: "number", mono: true },
      { k: "china_landing_price", label: "Landing Price ₹", type: "number", mono: true },
      { k: "china_margin_gst", label: "Importrend Margin GST ₹", type: "number", mono: true },
      { k: "china_last_mile_charges", label: "Last Mile Charges ₹", type: "number", mono: true },
      { k: "amazon_gst", label: "Amazon GST ₹", type: "number", mono: true },
      { k: "calculated_sp", label: "Suggestive SP ₹", type: "number", mono: true },
      // Feb 2026 spec — Final-Transfer-Price-driven aggregates + calculated %s
      { k: "final_importrend_margin", label: "Final Importrend Margin ₹", readonly: true, mono: true },
      { k: "final_reseller_commission", label: "Final Reseller Commission ₹", readonly: true, mono: true },
      { k: "importrend_margin_calculated_percentage", label: "Importrend Margin (calculated %)", readonly: true, mono: true },
      { k: "reseller_margin_calculated_percentage", label: "Reseller Margin (calculated %)", readonly: true, mono: true },
    ],
  },
  {
    key: "marketplace_details", title: "Marketplace", fields: [
      { k: "order_id", label: "Order ID" },
      { k: "order_item_id", label: "Order Item ID" },
      { k: "purchase_date", label: "Purchase Date", type: "date" },
      { k: "buyer_email", label: "Buyer Email" },
      { k: "buyer_phone_no", label: "Buyer Phone" },
      { k: "sku", label: "SKU" },
      { k: "product_name_marketplace", label: "Product Name" },
      { k: "qty_purchased", label: "Qty Purchased", type: "number" },
      { k: "item_price", label: "Item Price ₹", type: "number" },
      { k: "item_tax", label: "Item GST ₹", type: "number" },
      { k: "recipient_name", label: "Recipient Name" },
      { k: "ship_address_1", label: "Ship Address 1" },
      { k: "ship_address_2", label: "Ship Address 2" },
      { k: "ship_address_3", label: "Ship Address 3" },
      { k: "ship_city", label: "Ship City" },
      { k: "ship_state", label: "Ship State" },
      { k: "ship_postal_code", label: "Ship Postal Code" },
      { k: "ship_country_code", label: "Ship Country" },
      { k: "ship_phone_number", label: "Ship Phone Number" },
      { k: "bill_name", label: "Bill Name" },
      { k: "bill_address_1", label: "Bill Address 1" },
      { k: "bill_address_2", label: "Bill Address 2" },
      { k: "bill_address_3", label: "Bill Address 3" },
      { k: "bill_city", label: "Bill City" },
      { k: "bill_state", label: "Bill State" },
      { k: "bill_postal_code", label: "Bill Postal Code" },
      { k: "bill_country", label: "Bill Country" },
      { k: "bill_phone_number", label: "Bill Phone Number" },
      { k: "earliest_ship_date", label: "Earliest Ship Date", type: "date" },
      { k: "latest_ship_date", label: "Latest Ship Date", type: "date" },
      { k: "earliest_delivery_date", label: "Earliest Delivery Date", type: "date" },
      { k: "latest_delivery_date", label: "Latest Delivery Date", type: "date" },
      { k: "original_order_id", label: "Original Order ID" },
    ],
  },
  {
    key: "tax", title: "Tax", fields: [
      { k: "tax_description", label: "Tax Description" },
      { k: "hsn", label: "HSN", type: "number" },
      { k: "tax_rate", label: "Tax Rate %", type: "number", mono: true },
      { k: "chargeable_amount", label: "Chargeable ₹", type: "number", mono: true },
      { k: "qty", label: "Qty", type: "number", mono: true },
      { k: "tax_amount", label: "Tax Amount ₹", type: "number", mono: true },
      { k: "total_amount", label: "Total ₹", type: "number", mono: true },
      { k: "invoice_by_company", label: "Invoice By Company" },
    ],
  },
];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [order, setOrder] = useState(isNew ? { qty_purchased: 1 } : null);
  const [dropdowns, setDropdowns] = useState({});
  const [open, setOpen] = useState({ general: true, claim: false, shipping: false, price_update: false, china: true, marketplace_details: false, tax: false });
  const [busy, setBusy] = useState(false);
  const [fieldHistory, setFieldHistory] = useState(null); // {field, entries}
  const [customerHistory, setCustomerHistory] = useState(null); // {phone, ...profile}
  const [skuTimeline, setSkuTimeline] = useState(null); // {system_sku, summary, events}
  const [repeatInfo, setRepeatInfo] = useState(null); // {total_orders, phone}
  // Feb 25 2026 (Phase 2) — add-variant modal state. Each row spawns one
  // Procurement record on save.
  const [showVariants, setShowVariants] = useState(false);
  const [variants, setVariants] = useState([{ system_sku: "", variant: "", qty_to_order: "", image_url: "" }]);
  const [variantsBusy, setVariantsBusy] = useState(false);
  // Phase 3 — allocation history drawer
  const [allocHistory, setAllocHistory] = useState(null); // {events, ...}
  const { canView, canEdit, canDelete } = usePerms();
  const editable = canEdit("orders");
  const deletable = canDelete("orders");

  const openFieldHistory = async (fieldKey) => {
    if (isNew) return;
    try {
      const { data } = await api.get("/logs", { params: { module: "orders", record_id: id, field: fieldKey } });
      setFieldHistory({ field: fieldKey, entries: data });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { api.get("/settings/dropdowns").then((r) => setDropdowns(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => {
      toast.error(formatApiError(e));
      navigate("/orders");
    });
  }, [id, isNew, navigate]);

  // Repeat-customer probe: quietly fetch customer profile (DB read only).
  // AI LTV/churn scoring is now button-triggered — see `scoreCustomer` below.
  useEffect(() => {
    if (isNew || !order) return;
    const phone = (order.buyer_phone_no || order.ship_phone_number || "").trim();
    if (!phone) { setRepeatInfo(null); return; }
    Promise.all([
      api.get(`/customers/${encodeURIComponent(phone)}`).then((r) => r.data).catch(() => null),
      // Cached-only GET — no LLM call. If never generated, returns empty envelope.
      api.get(`/ai/customer-score/${encodeURIComponent(phone)}`).then((r) => r.data).catch(() => null),
    ]).then(([cust, score]) => {
      if (!cust) { setRepeatInfo(null); return; }
      const others = (cust.orders || []).filter((o) => o.id !== order.id).length;
      const hasScore = score && score.cached !== false;
      if (others > 0 || hasScore) {
        setRepeatInfo({
          phone,
          total_orders: cust.total_orders,
          previous_orders: others,
          ltv_score: hasScore ? score.ltv_score : null,
          churn_risk: hasScore ? score.churn_risk : null,
        });
      } else {
        setRepeatInfo(null);
      }
    });
  }, [order?.id, order?.buyer_phone_no, order?.ship_phone_number, isNew]); // eslint-disable-line

  const [scoringCustomer, setScoringCustomer] = useState(false);
  // Feb 25 2026 — shipping actions promoted to top-right action bar.
  const [printingLabel, setPrintingLabel] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const packagingVideoRef = React.useRef(null);

  const printShippingLabel = async () => {
    setPrintingLabel(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/shipping-label.pdf?ts=${Date.now()}`;
      const w = window.open(url, "_blank", "noopener");
      if (!w) toast.error("Popup blocked — allow popups to preview the label PDF.");
    } finally { setPrintingLabel(false); }
  };

  const uploadPackagingVideo = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video is larger than 100 MB — please compress first.");
      return;
    }
    setUploadingVideo(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/orders/${id}/packaging-video`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOrder((o) => ({
        ...o,
        packaging_video_url: data.packaging_video_url,
        packaging_video_thumb_url: data.packaging_video_thumb_url,
        packaging_video_uploaded_at: new Date().toISOString(),
        packaging_video_content_type: data.content_type,
        packaging_video_size_bytes: data.size_bytes,
      }));
      toast.success("Packaging video saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setUploadingVideo(false);
    if (packagingVideoRef.current) packagingVideoRef.current.value = "";
  };
  const scoreCustomer = async () => {
    const phone = (order?.buyer_phone_no || order?.ship_phone_number || "").trim();
    if (!phone) { toast.error("This order has no phone number to score."); return; }
    setScoringCustomer(true);
    try {
      const { data } = await api.post(`/ai/customer-score/${encodeURIComponent(phone)}/generate`);
      setRepeatInfo((prev) => ({
        ...(prev || { phone, total_orders: 0, previous_orders: 0 }),
        phone,
        ltv_score: data.ltv_score,
        churn_risk: data.churn_risk,
      }));
      toast.success(`LTV ${data.ltv_score} · Churn ${data.churn_risk}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setScoringCustomer(false);
  };

  const openCustomerHistory = async () => {
    if (!repeatInfo?.phone) return;
    try {
      const { data } = await api.get(`/customers/${encodeURIComponent(repeatInfo.phone)}`);
      setCustomerHistory(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openSkuTimeline = async () => {
    const sku = order?.system_sku;
    if (!sku) { toast.error("No System SKU on this order yet"); return; }
    try {
      const { data } = await api.get(`/procurement/sku/${encodeURIComponent(sku)}/timeline`);
      setSkuTimeline(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setF = (k, v) => setOrder((o) => ({ ...o, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/orders", order);
        toast.success(`Created #${data.system_id}`);
        navigate(`/orders/${data.id}`);
      } else {
        const { data } = await api.patch(`/orders/${id}`, order);
        setOrder(data);
        toast.success("Saved");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Deleted");
      navigate("/orders");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const recalc = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/recalc`);
      setOrder(data);
      toast.success("Recomputed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const releaseInventory = async () => {
    const qty = order.inventory_allocated_qty;
    if (!qty) { toast.error("No stock is currently allocated to this order"); return; }
    if (!window.confirm(
      `Release ${qty} unit(s) of ${order.system_sku || "this SKU"} back into inventory?\nThe order's working_status will drop back from Daily-Ship and an entry will be added to the SKU's allocation history.`,
    )) return;
    try {
      const { data } = await api.post(`/orders/${id}/release-inventory`);
      setOrder(data.order);
      toast.success(`Released ${data.released_qty} units back to stock`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const releaseProcurement = async () => {
    const qty = order.procurement_allocated_qty;
    if (!qty) { toast.error("No procurement is currently allocated to this order"); return; }
    if (!window.confirm(
      `Release ${qty} unit(s) allocated from procurement back to the source procurement row(s)?\nThe order's working_status will drop back from "Procurement-Allocated".`,
    )) return;
    try {
      const { data } = await api.post(`/orders/${id}/release-procurement`, {});
      setOrder(data.order);
      toast.success(`Released ${data.released_qty} units back to procurement`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reallocate = async () => {
    if (!window.confirm(
      "Re-run the allocation pipeline for this order?\nExisting procurement allocations will be released and the order will attempt inventory first, then oldest open procurement rows.",
    )) return;
    try {
      const { data } = await api.post(`/orders/${id}/reallocate`);
      setOrder(data.order);
      toast.success(
        `Reallocated. Inventory: ${data.inventory_allocated_qty} · Procurement: ${data.procurement_allocated_qty}`,
      );
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAllocHistory = async () => {
    try {
      const { data } = await api.get(`/orders/${id}/allocation-history`);
      setAllocHistory(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeProcurementEvent = async (procurementId) => {
    if (!window.confirm(
      `Remove the allocation against procurement ${procurementId}?\nThe qty will be released back to that procurement row.`,
    )) return;
    try {
      const { data } = await api.post(`/orders/${id}/release-procurement`, {
        procurement_id: procurementId,
      });
      setOrder(data.order);
      // Refresh history drawer
      const { data: hist } = await api.get(`/orders/${id}/allocation-history`);
      setAllocHistory(hist);
      toast.success(`Released ${data.released_qty} units`);
    } catch (e) { toast.error(formatApiError(e)); }
  };


  if (!order) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <>
      <PageHeader
        title={isNew ? "New Order" : `Order #${order.system_id ?? ""}`}
        subtitle={isNew ? "Create a new order manually" : `China ID: ${order.china_id ?? "—"}`}
        testId={T.orderDetail}
      >
        <Button
          onClick={() => {
            // If this tab was opened via window.open from the Orders list
            // (window.opener present), just close it so the user returns to
            // their filtered list still on the original tab. Otherwise fall
            // back to navigating to /orders in-place.
            if (window.opener && window.opener !== window) {
              window.close();
            } else {
              navigate("/orders");
            }
          }}
          testId="order-detail-back-btn"
        ><ArrowLeft size={13} /> Back</Button>
        {!isNew && editable && <Button onClick={recalc}><RotateCcw size={13} /> Recompute</Button>}
        {!isNew && editable && (
          <Button onClick={() => { setShowVariants(true); setVariants([{ system_sku: "", variant: "", qty_to_order: "", image_url: "" }]); }} testId="order-add-variant-btn">
            <Plus size={13} /> Add Variant
          </Button>
        )}
        {!isNew && editable && <Button variant="danger" onClick={del} testId={T.deleteBtn}><Trash2 size={13} /> Delete</Button>}
        {editable && <Button variant="primary" onClick={save} disabled={busy} testId={T.saveBtn}><Save size={13} /> {busy ? "Saving…" : "Save"}</Button>}
      </PageHeader>
      <PageBody>
        {!isNew && (() => {
          const pills = [
            { k: "working_status", v: order.working_status },
            { k: "marketplace_status", v: order.marketplace_status },
            { k: "shipping_status", v: order.shipping_status },
            { k: "china_order_status", v: order.china_order_status },
            { k: "claim_status", v: order.claim_status },
          ].filter((p) => canView("orders", p.k));
          const showTransfer = canView("orders", "china_transfer_price");
          const showSp = canView("orders", "calculated_sp");
          const allocQty = Number(order.inventory_allocated_qty || 0);
          const procAllocQty = Number(order.procurement_allocated_qty || 0);
          const showAllocation = !!order.system_sku && canView("orders", "system_sku");
          if (!pills.length && !showTransfer && !showSp && !showAllocation) return null;
          return (
            <div className="mb-4 flex flex-wrap gap-2 items-center">
              {pills.map((p) => <StatusPill key={p.k} value={p.v} tone={statusTone(p.v)} />)}
              {showAllocation && (
                <span
                  data-testid="order-inventory-allocated-pill"
                  className={`inline-flex items-center gap-1.5 h-6 px-2 text-[10.5px] font-bold uppercase tracking-wider rounded-sm border ${
                    allocQty > 0
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                      : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                  title={allocQty > 0
                    ? `Allocated ${allocQty} unit(s) from ${order.inventory_allocated_warehouse || "default warehouse"}`
                    : "No inventory currently allocated to this order"}
                >
                  Inventory Allocated: {allocQty > 0 ? `Yes · ${allocQty}` : "No"}
                </span>
              )}
              {allocQty > 0 && editable && (
                <button
                  type="button"
                  onClick={releaseInventory}
                  data-testid="order-release-inventory-btn"
                  className="h-6 px-2 text-[10.5px] font-semibold rounded-sm inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  title="Return the allocated quantity to inventory"
                >
                  Release Stock
                </button>
              )}
              {showAllocation && procAllocQty > 0 && (
                <span
                  data-testid="order-procurement-allocated-pill"
                  className="inline-flex items-center gap-1.5 h-6 px-2 text-[10.5px] font-bold uppercase tracking-wider rounded-sm border bg-indigo-50 text-indigo-800 border-indigo-300"
                  title={`Allocated ${procAllocQty} unit(s) from open procurement rows`}
                >
                  Procurement Allocated: Yes · {procAllocQty}
                </span>
              )}
              {procAllocQty > 0 && editable && (
                <button
                  type="button"
                  onClick={releaseProcurement}
                  data-testid="order-release-procurement-btn"
                  className="h-6 px-2 text-[10.5px] font-semibold rounded-sm inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  title="Return the allocated qty to source procurement row(s)"
                >
                  Release Procurement
                </button>
              )}
              {showAllocation && editable && (
                <button
                  type="button"
                  onClick={reallocate}
                  data-testid="order-reallocate-btn"
                  className="h-6 px-2 text-[10.5px] font-semibold rounded-sm inline-flex items-center gap-1 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  title="Retry allocation: inventory first, then oldest procurement rows"
                >
                  Reallocate
                </button>
              )}
              {showAllocation && (allocQty > 0 || procAllocQty > 0) && (
                <button
                  type="button"
                  onClick={openAllocHistory}
                  data-testid="order-allocation-history-btn"
                  className="h-6 px-2 text-[10.5px] font-semibold rounded-sm inline-flex items-center gap-1 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  title="View allocation events for this order"
                >
                  <History size={11} /> History
                </button>
              )}
              {(showTransfer || showSp) && (
                <span className="text-[11px] text-slate-500 font-mono ml-2">
                  {showTransfer && <>Transfer ₹{Number(order.china_transfer_price || 0).toFixed(2)}</>}
                  {showTransfer && showSp && " • "}
                  {showSp && <>SP ₹{Number(order.calculated_sp || 0).toFixed(2)}</>}
                </span>
              )}
            </div>
          );
        })()}

        {!isNew && (() => {
          const showImage = canView("orders", "image_url");
          const showSku = canView("orders", "system_sku");
          const showMpId = canView("orders", "marketplace_identifier");
          const showQty = canView("orders", "qty_purchased");
          const showItemPrice = canView("orders", "item_price");
          const showProductUrl = canView("orders", "product_url");
          const showProductName = canView("orders", "product_name");
          const showRepeat = repeatInfo && canView("customers");
          const showSkuHistory = order.system_sku && canView("orders", "system_sku");
          const hasAnyContent = showImage || showSku || showMpId || showQty || showItemPrice || showProductUrl || showProductName || showRepeat || showSkuHistory;
          if (!hasAnyContent) return null;
          return (
            <div className="mb-4 bg-white border border-slate-200 rounded-sm p-4 flex gap-5 items-start" data-testid="order-hero">
              {showImage && (
                <div className="w-48 h-48 shrink-0 bg-slate-50 border border-slate-200 rounded-sm flex items-center justify-center overflow-hidden">
                  {order.image_url ? (
                    <ImageThumb src={order.image_url} alt={order.product_name || ""} size={192} fit="contain" className="!bg-slate-50" testId="order-hero-image" />
                  ) : (
                    <div className="text-slate-300 text-[11px] font-mono">no image</div>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                  {canView("orders", "marketplace") ? (order.marketplace || "—") : ""} · #{order.system_id ?? "—"}
                </div>
                {showProductName && (
                  <div className="font-heading text-[18px] font-semibold text-slate-900 mt-0.5 leading-snug">
                    {order.product_name || <span className="text-slate-400 italic">Unnamed product</span>}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap gap-3 text-[11.5px] text-slate-600">
                  {showSku && <span><span className="text-slate-500">SKU:</span> <span className="font-mono">{order.system_sku || "—"}</span></span>}
                  {showMpId && <span><span className="text-slate-500">MP-ID:</span> <span className="font-mono">{order.marketplace_identifier || "—"}</span></span>}
                  {showQty && <span><span className="text-slate-500">Qty:</span> <span className="font-mono font-semibold">{order.qty_purchased ?? "—"}</span></span>}
                  {showItemPrice && <span><span className="text-slate-500">Item Price:</span> <span className="font-mono">₹{order.item_price ?? "—"}</span></span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  {showRepeat && (
                    <button
                      onClick={openCustomerHistory}
                      data-testid="repeat-customer-badge"
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11.5px] font-semibold rounded-sm border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-900 hover:from-amber-100 hover:to-orange-100"
                      title={`${repeatInfo.previous_orders} previous order${repeatInfo.previous_orders === 1 ? "" : "s"}${repeatInfo.ltv_score != null ? ` · LTV ${repeatInfo.ltv_score}/100 · Churn ${repeatInfo.churn_risk}` : ""}`}
                    >
                      <Repeat size={12} />
                      Repeat · {repeatInfo.total_orders} orders
                      <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[9.5px] font-bold text-white bg-amber-600 rounded-full">{repeatInfo.previous_orders}</span>
                      {repeatInfo.ltv_score != null && (
                        <>
                          <span className="mx-1 text-amber-400">·</span>
                          <span
                            className={`inline-flex items-center gap-1 h-5 px-1.5 text-[10.5px] font-bold rounded-sm ${
                              repeatInfo.ltv_score >= 70 ? "bg-emerald-600 text-white" :
                              repeatInfo.ltv_score >= 40 ? "bg-amber-500 text-white" :
                              "bg-slate-500 text-white"
                            }`}
                          >
                            LTV {repeatInfo.ltv_score}
                          </span>
                        </>
                      )}
                      {repeatInfo.churn_risk && repeatInfo.churn_risk !== "unknown" && (
                        <span
                          className={`ml-0.5 inline-flex items-center h-5 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
                            repeatInfo.churn_risk === "high" ? "bg-red-100 text-red-800 border-red-300" :
                            repeatInfo.churn_risk === "medium" ? "bg-amber-100 text-amber-800 border-amber-300" :
                            repeatInfo.churn_risk === "new" ? "bg-blue-100 text-blue-800 border-blue-300" :
                            "bg-emerald-100 text-emerald-800 border-emerald-300"
                          }`}
                          title={`Churn risk: ${repeatInfo.churn_risk}`}
                        >
                          {repeatInfo.churn_risk === "high" && "⚠ "}
                          {repeatInfo.churn_risk === "new" && "★ "}
                          {repeatInfo.churn_risk}
                        </span>
                      )}
                    </button>
                  )}
                  {/* Feb 25 2026: manual AI scoring button — only render when we have a phone and either
                       (a) no repeatInfo yet OR (b) repeatInfo exists but ltv_score is null (never scored). */}
                  {(order.buyer_phone_no || order.ship_phone_number) && (!repeatInfo || repeatInfo.ltv_score == null) && (
                    <button
                      onClick={scoreCustomer}
                      disabled={scoringCustomer}
                      data-testid="order-score-customer-btn"
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11.5px] font-semibold rounded-sm border border-violet-300 bg-violet-50 text-violet-900 hover:bg-violet-100 disabled:opacity-50"
                      title="Run AI LTV + churn scoring for this customer"
                    >
                      {scoringCustomer ? "Scoring…" : "Score customer"}
                    </button>
                  )}
                  {showSkuHistory && (
                    <button
                      onClick={openSkuTimeline}
                      data-testid="sku-history-btn"
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11.5px] font-semibold rounded-sm border border-indigo-300 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
                    >
                      <Package size={12} /> SKU procurement history
                    </button>
                  )}
                  {showProductUrl && order.product_url && (
                    <a href={order.product_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-7 px-2.5 text-[11.5px] text-blue-700 hover:underline font-semibold border border-transparent" data-testid="order-product-url">
                      View on {(canView("orders", "marketplace") && order.marketplace) || "marketplace"} →
                    </a>
                  )}
                  {/* Feb 25 2026 — shipping actions on top-right so ops can trigger them
                       without expanding the Shipping section. `Print Label` opens the
                       100x150mm PDF in a new tab; `Upload packaging video` posts to
                       object storage and links the URL onto the order. */}
                  {!isNew && (
                    <button
                      onClick={printShippingLabel}
                      disabled={printingLabel}
                      data-testid="order-print-label-btn"
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11.5px] font-semibold rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      title="Print 100x150mm shipping label"
                    >
                      <Printer size={12} /> {printingLabel ? "Preparing…" : "Print shipping label"}
                    </button>
                  )}
                  {!isNew && (order.packaging_video_url ? (() => {
                    // Feb 26 2026 — video streamed via our own backend
                    // (`/api/orders/{oid}/packaging-video.mp4`). Attach the
                    // auth token as `?auth=<token>` so the plain <video> tag
                    // can load it (browsers can't attach headers to media).
                    // Aug 6 2026 — the thumbnail is now a real ffmpeg-extracted
                    // JPG served via its own endpoint, rendered as a plain
                    // <img> (loads instantly, no video decode needed). Falls
                    // back to the old #t=0.5 <video> trick only for legacy
                    // orders uploaded before thumbnails existed.
                    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                    const tok = localStorage.getItem("access_token") || "";
                    const withAuth = (path) => {
                      const base = path.startsWith("http") ? path : `${API}${path}`;
                      return `${base}${base.includes("?") ? "&" : "?"}auth=${encodeURIComponent(tok)}`;
                    };
                    const src = withAuth(order.packaging_video_url);
                    const thumbSrc = order.packaging_video_thumb_url ? withAuth(order.packaging_video_thumb_url) : null;
                    return (
                    <div className="inline-flex items-center gap-2" data-testid="packaging-video-cell">
                      <a
                        href={src}
                        target="_blank"
                        rel="noreferrer"
                        title="Open packaging video in new tab"
                        data-testid="packaging-video-link"
                        className="block"
                      >
                        {thumbSrc ? (
                          <img
                            src={thumbSrc}
                            alt="Packaging video thumbnail"
                            className="h-9 w-14 object-cover border border-emerald-300 rounded-sm bg-slate-100"
                          />
                        ) : (
                          <video
                            src={`${src}#t=0.5`}
                            preload="metadata"
                            muted
                            playsInline
                            className="h-9 w-14 object-cover border border-emerald-300 rounded-sm bg-slate-100"
                          />
                        )}
                      </a>
                      <div className="inline-flex flex-col gap-1">
                        <a
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                        >
                          <Video size={11} /> View
                        </a>
                        {editable && (
                          <>
                            <input ref={packagingVideoRef} type="file" accept="video/*" onChange={uploadPackagingVideo} className="hidden"
                                   data-testid="packaging-video-input" />
                            <button
                              onClick={() => packagingVideoRef.current?.click()}
                              disabled={uploadingVideo}
                              data-testid="packaging-video-replace-btn"
                              className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              title="Replace packaging video"
                            >
                              <Upload size={11} /> {uploadingVideo ? "…" : "Replace"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })() : editable && (
                    <>
                      <input ref={packagingVideoRef} type="file" accept="video/*" onChange={uploadPackagingVideo} className="hidden"
                             data-testid="packaging-video-input" />
                      <button
                        onClick={() => packagingVideoRef.current?.click()}
                        disabled={uploadingVideo}
                        data-testid="packaging-video-upload-btn"
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11.5px] font-semibold rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        title="Upload packaging video for this order"
                      >
                        <Upload size={12} /> {uploadingVideo ? "Uploading…" : "Upload packaging video"}
                      </button>
                    </>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
        {SECTIONS.map((sec) => {
          const visibleFields = sec.fields.filter((f) => canView("orders", f.k));
          if (visibleFields.length === 0) return null;
          return (
          <div key={sec.key} className="bg-white border border-slate-200 rounded-sm mb-4">
            <button
              data-testid={T.orderSection(sec.key)}
              onClick={() => setOpen((o) => ({ ...o, [sec.key]: !o[sec.key] }))}
              className="w-full flex justify-between items-center px-4 py-2 border-b border-slate-200 bg-slate-50 hover:bg-slate-100"
            >
              <span className="font-heading text-[13px] font-semibold text-slate-900">{sec.title}</span>
              <span className="text-[10.5px] uppercase tracking-wider text-slate-500">{open[sec.key] ? "Hide" : "Show"}</span>
            </button>
            {open[sec.key] && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {visibleFields.map((f) => {
                  const fieldEditable = editable && canEdit("orders", f.k) && !f.readonly;
                  return (
                  <Field key={f.k} label={
                    <span className="inline-flex items-center gap-1">
                      {f.label}
                      {!isNew && (
                        <button
                          data-testid={`field-history-${f.k}`}
                          onClick={() => openFieldHistory(f.k)}
                          className="text-slate-400 hover:text-blue-600"
                          title={`History for ${f.label}`}
                        >
                          <History size={11} />
                        </button>
                      )}
                    </span>
                  } span={f.span || 1}>
                    {f.type === "select" ? (
                      <Select testId={T.formInput(f.k)} options={f.opts?.(dropdowns) || []} value={order[f.k]} onChange={(v) => setF(f.k, v)} disabled={!fieldEditable} />
                    ) : f.type === "image" ? (
                      <ImageInput testId={T.formInput(f.k)} value={order[f.k]} onChange={(v) => setF(f.k, v)} />
                    ) : f.type === "textarea" ? (
                      <Textarea testId={T.formInput(f.k)} value={order[f.k] || ""} onChange={(e) => setF(f.k, e.target.value)} disabled={!fieldEditable} className={!fieldEditable ? "readonly-field" : ""} />
                    ) : (
                      <Input
                        testId={T.formInput(f.k)}
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        readOnly={!fieldEditable}
                        className={`${!fieldEditable ? "readonly-field" : ""} ${f.mono ? "font-mono" : ""}`}
                        // Feb 27 2026 — coerce non-ISO date values (e.g. Flipkart's
                        // "Aug 02, 2026") to ISO YYYY-MM-DD so <input type="date">
                        // can display them. Non-date inputs pass through unchanged.
                        value={f.type === "date" ? _toIsoDate(order[f.k]) : (order[f.k] ?? "")}
                        onChange={(e) => setF(f.k, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                      />
                    )}
                    {typeof f.hint === "function" && (() => {
                      const h = f.hint(order, f.k);
                      if (!h) return null;
                      const targetHref = h.target?.kind === "procurement" ? "/procurement" :
                                          h.target?.kind === "order" && h.target?.id ? `/orders/${h.target.id}` :
                                          h.target?.kind === "inventory" ? "/inventory" : null;
                      return (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-sm px-1.5 py-0.5"
                             data-testid={`lifo-hint-${f.k}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                          <span>{h.text}</span>
                          {targetHref && (
                            <a href={targetHref} target="_blank" rel="noreferrer"
                               className="ml-1 text-indigo-800 underline hover:text-indigo-900"
                               data-testid={`lifo-jump-${f.k}`}>view</a>
                          )}
                        </div>
                      );
                    })()}
                  </Field>
                  );
                })}
                {sec.key === "shipping" && !isNew && (
                  <ShippingExtras
                    order={order}
                    setF={setF}
                    editable={editable}
                    dropdowns={dropdowns}
                  />
                )}
              </div>
            )}
          </div>
          );
        })}
      </PageBody>

      {fieldHistory && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={() => setFieldHistory(null)}>
          <div className="w-[560px] h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold">Field History</div>
                <div className="font-mono text-[11px] text-slate-500">{fieldHistory.field}</div>
              </div>
              <button onClick={() => setFieldHistory(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="erp-table">
                <thead>
                  <tr><th>Timestamp (IST)</th><th>User</th><th>From</th><th>To</th></tr>
                </thead>
                <tbody>
                  {(fieldHistory.entries || []).map((e) => {
                    const change = (e.changes || []).find((c) => c.field === fieldHistory.field);
                    if (!change) return null;
                    return (
                      <tr key={e.id}>
                        <td className="font-mono whitespace-nowrap" title={e.timestamp}>{_fmtIST(e.timestamp)}</td>
                        <td>{e.user_email || "—"}</td>
                        <td className="text-red-700 font-mono">{String(change.from ?? "—").slice(0, 40)}</td>
                        <td className="text-emerald-700 font-mono">{String(change.to ?? "—").slice(0, 40)}</td>
                      </tr>
                    );
                  })}
                  {!(fieldHistory.entries || []).length && (
                    <tr><td colSpan={4} className="text-center py-6 text-slate-500">No changes to this field yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {customerHistory && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={() => setCustomerHistory(null)}>
          <div className="w-[860px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold flex items-center gap-2">
                  <Repeat size={15} className="text-amber-600" /> Customer History
                </div>
                <div className="font-mono text-[11px] text-slate-500">
                  {customerHistory.name || "—"} · {customerHistory.phone}
                </div>
              </div>
              <button onClick={() => setCustomerHistory(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-4 gap-2 p-4 bg-slate-50 border-b border-slate-200">
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Orders</div>
                <div className="font-mono text-lg text-slate-900 font-bold">{customerHistory.total_orders}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Lifetime Spend</div>
                <div className="font-mono text-lg text-emerald-800 font-bold">₹{customerHistory.total_spend?.toLocaleString?.() || 0}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">RTO Rate</div>
                <div className={`font-mono text-lg font-bold ${customerHistory.rto_rate > 20 ? "text-red-700" : "text-slate-700"}`}>{customerHistory.rto_rate}%</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Refund Rate</div>
                <div className={`font-mono text-lg font-bold ${customerHistory.refund_rate > 15 ? "text-amber-700" : "text-slate-700"}`}>{customerHistory.refund_rate}%</div>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <table className="erp-table" data-testid="customer-orders-history">
                <thead>
                  <tr>
                    <th>System ID</th>
                    <th>Date</th>
                    <th>Marketplace</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Item ₹</th>
                    <th>MP Status</th>
                    <th>Shipping</th>
                    <th>Refund</th>
                  </tr>
                </thead>
                <tbody>
                  {(customerHistory.orders || []).map((o) => (
                    <tr key={o.id} onClick={() => navigate(`/orders/${o.id}`)} className={`cursor-pointer ${o.id === order?.id ? "bg-amber-50" : ""}`}>
                      <td className="font-mono">#{o.system_id}{o.id === order?.id && <span className="ml-1 text-[9.5px] font-bold text-amber-700 uppercase">(current)</span>}</td>
                      <td className="font-mono">{o.purchase_date || "—"}</td>
                      <td>{o.marketplace || "—"}</td>
                      <td className="truncate max-w-[220px]">{o.product_name || "—"}</td>
                      <td className="font-mono">{o.qty_purchased || 1}</td>
                      <td className="font-mono">₹{Number(o.item_price || 0).toLocaleString()}</td>
                      <td><StatusPill value={o.marketplace_status} tone={statusTone(o.marketplace_status)} /></td>
                      <td><StatusPill value={o.shipping_status} tone={statusTone(o.shipping_status)} /></td>
                      <td><StatusPill value={o.refund_status} tone={statusTone(o.refund_status)} /></td>
                    </tr>
                  ))}
                  {!(customerHistory.orders || []).length && (
                    <tr><td colSpan={9} className="text-center py-6 text-slate-500">No orders on record.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {skuTimeline && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={() => setSkuTimeline(null)}>
          <div className="w-[900px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold flex items-center gap-2">
                  <Package size={15} className="text-indigo-600" /> SKU Procurement Timeline
                </div>
                <div className="font-mono text-[11px] text-slate-500">{skuTimeline.system_sku}</div>
              </div>
              <button onClick={() => setSkuTimeline(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            {skuTimeline.summary && (
              <div className="grid grid-cols-4 gap-2 p-4 bg-slate-50 border-b border-slate-200">
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Events</div>
                  <div className="font-mono text-lg text-slate-900 font-bold">{skuTimeline.summary.events}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg Transit</div>
                  <div className="font-mono text-lg text-blue-800 font-bold">{skuTimeline.summary.avg_transit_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg Placed→Recv</div>
                  <div className="font-mono text-lg text-indigo-800 font-bold">{skuTimeline.summary.avg_total_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last Transit</div>
                  <div className="font-mono text-lg text-emerald-800 font-bold">{skuTimeline.summary.last_transit_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
              </div>
            )}
            <SkuTimelineTrends events={skuTimeline.events || []} />
            <div className="flex-1 overflow-auto p-4">
              <table className="erp-table" data-testid="sku-timeline-in-order">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>System #</th>
                    <th>Status</th>
                    <th>Qty</th>
                    <th>Price ¥</th>
                    <th>Shipping ¥</th>
                    <th>Wt (g)</th>
                    <th>Order Date</th>
                    <th>Dispatched</th>
                    <th>Received</th>
                    <th>Transit</th>
                    <th>Shipment ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(skuTimeline.events || []).map((e) => (
                    <tr key={`${e.source}-${e.id}`} className={e.id === order?.id ? "bg-indigo-50" : ""}>
                      <td>
                        <span className={`inline-flex px-1.5 h-5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${e.source === "order" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                          {e.source === "order" ? "Order" : "Direct"}
                        </span>
                      </td>
                      <td className="font-mono">{e.system_id ? `#${e.system_id}` : "—"}{e.id === order?.id && <span className="ml-1 text-[9.5px] font-bold text-indigo-700 uppercase">(current)</span>}</td>
                      <td><StatusPill value={e.china_order_status} tone={statusTone(e.china_order_status)} /></td>
                      <td className="font-mono">{e.china_qty_to_order ?? "—"}</td>
                      <td className="font-mono">{e.china_price ?? "—"}</td>
                      <td className="font-mono">{e.china_shipping ?? "—"}</td>
                      <td className="font-mono">{e.china_weight_gram ?? "—"}</td>
                      <td className="font-mono">{e.china_order_date || "—"}</td>
                      <td className="font-mono">{e.china_order_dispatched_date || "—"}</td>
                      <td className="font-mono">{e.china_order_received_date || "—"}</td>
                      <td className="font-mono text-blue-800 font-semibold">{e.transit_days !== null && e.transit_days !== undefined ? `${e.transit_days}d` : "—"}</td>
                      <td className="font-mono text-indigo-800">{e.china_shipment_id || "—"}</td>
                    </tr>
                  ))}
                  {!(skuTimeline.events || []).length && (
                    <tr><td colSpan={12} className="text-center py-6 text-slate-500">No procurement events yet for this SKU.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {showVariants && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !variantsBusy && setShowVariants(false)}
          data-testid="order-variants-modal"
        >
          <div className="bg-white rounded-sm border-2 border-indigo-500 shadow-2xl max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-indigo-200 bg-indigo-50 px-4 py-3">
              <Plus size={18} className="text-indigo-700" />
              <h2 className="font-heading text-[14px] font-bold text-indigo-900">Add Procurement Variants</h2>
              <span className="text-[11px] text-slate-500 ml-2">One row → one new Procurement record. Copies Source URL / China Price / China Shipping / MOQ / Weight / Status from this order.</span>
            </div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-[1fr_1fr_100px_1.5fr_32px] gap-2 text-[10px] font-heading font-bold uppercase tracking-wider text-slate-500 px-1">
                <div>System SKU <span className="normal-case text-slate-400 font-normal">(optional)</span></div>
                <div>Variant</div>
                <div>Qty to Order</div>
                <div>Image URL</div>
                <div></div>
              </div>
              {variants.map((v, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_100px_1.5fr_32px] gap-2 items-center">
                  <input
                    type="text"
                    data-testid={`variant-sku-${i}`}
                    value={v.system_sku}
                    onChange={(e) => setVariants((vs) => vs.map((x, idx) => idx === i ? { ...x, system_sku: e.target.value } : x))}
                    placeholder={order.system_sku || ""}
                    className="h-8 px-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:border-indigo-500 font-mono"
                  />
                  <input
                    type="text"
                    data-testid={`variant-variant-${i}`}
                    value={v.variant}
                    onChange={(e) => setVariants((vs) => vs.map((x, idx) => idx === i ? { ...x, variant: e.target.value } : x))}
                    placeholder="e.g. S / Red"
                    className="h-8 px-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:border-indigo-500"
                  />
                  <input
                    type="number"
                    data-testid={`variant-qty-${i}`}
                    value={v.qty_to_order}
                    onChange={(e) => setVariants((vs) => vs.map((x, idx) => idx === i ? { ...x, qty_to_order: e.target.value } : x))}
                    min={1}
                    className="h-8 px-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:border-indigo-500 text-right font-mono"
                  />
                  <input
                    type="url"
                    data-testid={`variant-image-${i}`}
                    value={v.image_url}
                    onChange={(e) => setVariants((vs) => vs.map((x, idx) => idx === i ? { ...x, image_url: e.target.value } : x))}
                    placeholder="https://cbu01.alicdn.com/..."
                    className="h-8 px-2 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    onClick={() => setVariants((vs) => vs.filter((_, idx) => idx !== i))}
                    disabled={variants.length === 1 || variantsBusy}
                    data-testid={`variant-remove-${i}`}
                    className="h-8 w-8 flex items-center justify-center text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Remove row"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setVariants((vs) => [...vs, { system_sku: "", variant: "", qty_to_order: "", image_url: "" }])}
                disabled={variantsBusy}
                data-testid="variant-add-row-btn"
                className="mt-2 h-7 px-2 text-[11px] font-semibold inline-flex items-center gap-1 text-indigo-700 border border-indigo-200 rounded-sm hover:bg-indigo-50 disabled:opacity-40"
              >
                <Plus size={12} /> Add another variant
              </button>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button onClick={() => setShowVariants(false)} disabled={variantsBusy} testId="variant-cancel-btn">Cancel</Button>
              <button
                onClick={async () => {
                  const rows = variants.filter((v) => Number(v.qty_to_order) > 0);
                  if (!rows.length) { toast.error("Enter Qty to Order (> 0) for at least one variant."); return; }
                  setVariantsBusy(true);
                  try {
                    const payload = {
                      variants: rows.map((v) => ({
                        system_sku: v.system_sku.trim() || null,
                        variant: v.variant.trim() || null,
                        qty_to_order: Number(v.qty_to_order),
                        image_url: v.image_url.trim() || null,
                      })),
                    };
                    const { data } = await api.post(`/orders/${order.id}/procurement-variants`, payload);
                    toast.success(`Created ${data.created} procurement record(s). China IDs: ${data.china_ids.join(", ")}`);
                    setShowVariants(false);
                  } catch (e) { toast.error(formatApiError(e)); }
                  setVariantsBusy(false);
                }}
                disabled={variantsBusy}
                data-testid="variant-save-btn"
                className="h-8 px-3 text-[12px] font-bold uppercase tracking-wider rounded-sm inline-flex items-center gap-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={12} /> {variantsBusy ? "Saving…" : "Save Variants"}
              </button>
            </div>
          </div>
        </div>
      )}

      {allocHistory && (
        <div className="fixed inset-0 bg-slate-950/40 z-50 flex items-center justify-center p-4" onClick={() => setAllocHistory(null)}>
          <div className="bg-white rounded-sm shadow-2xl border border-slate-200 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="allocation-history-modal">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 bg-slate-50">
              <div>
                <div className="text-[13px] font-bold uppercase tracking-wider text-slate-800">Allocation History</div>
                <div className="font-mono text-[11px] text-slate-500">Order #{allocHistory.system_id} · {allocHistory.system_sku} · Demand {allocHistory.qty_purchased} · Inv {allocHistory.inventory_allocated_qty} · Proc {allocHistory.procurement_allocated_qty}</div>
              </div>
              <button onClick={() => setAllocHistory(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-[11px] font-mono border-collapse">
                <thead>
                  <tr className="text-left text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <th className="py-1 pr-3">Kind</th>
                    <th className="py-1 pr-3">Qty</th>
                    <th className="py-1 pr-3">Source</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Allocated At</th>
                    <th className="py-1 pr-3">By</th>
                    <th className="py-1 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(allocHistory.events || []).map((e, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`alloc-history-row-${i}`}>
                      <td className="py-1.5 pr-3">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-sm ${e.kind === "inventory" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-indigo-50 text-indigo-800 border border-indigo-200"}`}>{e.kind}</span>
                      </td>
                      <td className="py-1.5 pr-3 font-bold">{e.qty}</td>
                      <td className="py-1.5 pr-3">
                        {e.kind === "inventory"
                          ? (e.warehouse_name || "—")
                          : (e.procurement_china_id ? `China #${e.procurement_china_id}` : "—")}
                      </td>
                      <td className="py-1.5 pr-3 text-slate-600">{e.china_order_status || "—"}</td>
                      <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap" title={e.allocated_at}>{_fmtIST(e.allocated_at)}</td>
                      <td className="py-1.5 pr-3 text-slate-500">{e.allocated_by || "—"}</td>
                      <td className="py-1.5 pr-3">
                        {e.kind === "procurement" && e.procurement_id && editable && (
                          <button
                            onClick={() => removeProcurementEvent(e.procurement_id)}
                            data-testid={`alloc-history-remove-${i}`}
                            className="h-6 px-2 text-[10px] font-semibold rounded-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                            title="Release this procurement allocation"
                          >Remove</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!(allocHistory.events || []).length && (
                    <tr><td colSpan={7} className="py-6 text-center text-slate-400">No allocation events yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              {editable && (
                <Button onClick={async () => { await reallocate(); const { data } = await api.get(`/orders/${id}/allocation-history`); setAllocHistory(data); }} testId="alloc-history-reallocate-btn">
                  <Repeat size={12} /> Reallocate
                </Button>
              )}
              <Button onClick={() => setAllocHistory(null)} testId="alloc-history-close-btn">Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
